import { getWalletConfigSetting } from '@/configs';
import { providers } from 'ethers';
import { useMemo } from 'react';
import type { Account, Chain, Client, PublicClient, Transport, WalletClient } from 'viem';

import { Config, useClient, useConnectorClient, usePublicClient, useWalletClient } from 'wagmi';

export function clientToProvider(
  client: Client<Transport, Chain>,
): providers.FallbackProvider | providers.JsonRpcProvider {
  const { chain, transport } = client;
  const network = {
    chainId: chain.id,
    name: chain.name,
    ensAddress: chain.contracts?.ensRegistry?.address,
  };
  if (transport.type === 'fallback')
    return new providers.FallbackProvider(
      (transport.transports as ReturnType<Transport>[]).map(
        ({ value }) => new providers.JsonRpcProvider(value?.url, network),
      ),
    );
  return new providers.JsonRpcProvider(transport.url, network);
}

export function clientToSigner(client: Client<Transport, Chain, Account>): providers.JsonRpcSigner {
  const { account, chain, transport } = client;
  const network = {
    chainId: chain.id,
    name: chain.name,
    ensAddress: chain.contracts?.ensRegistry?.address,
  };
  const provider = new providers.Web3Provider(transport, network);
  const signer = provider.getSigner(account.address);
  return signer;
}

// // Helper function to ensure ChainKit is initialized for a given chainId
// function ensureChainKitInitialized(chainId: number): void {
//   try {
//     // This will initialize the ChainKit and register base tokens if not already done
//     getChainKit(chainId as CHAIN_ID);
//   } catch (error) {
//     console.warn(`[wagmi] Failed to initialize ChainKit for chainId ${chainId}:`, error);
//   }
// }

// New viem-based hooks with ChainKit integration
/** Hook to get viem PublicClient directly with ChainKit initialization */
export function useViemPublicClient({ chainId }: { chainId?: number | undefined } = {}): PublicClient | undefined {
  const client = usePublicClient({ chainId });

  // // Initialize ChainKit when client is available
  // useMemo(() => {
  //   if (client?.chain?.id) {
  //     ensureChainKitInitialized(client.chain.id);
  //   }
  // }, [client?.chain?.id]);

  return client;
}

/** Hook to get viem WalletClient directly with ChainKit initialization */
export function useViemWalletClient({ chainId }: { chainId?: number } = {}): WalletClient | undefined {
  const { data: walletClient } = useWalletClient({ chainId });

  // // Initialize ChainKit when client is available
  // useMemo(() => {
  //   if (walletClient?.chain?.id) {
  //     ensureChainKitInitialized(walletClient.chain.id);
  //   }
  // }, [walletClient?.chain?.id]);

  return walletClient;
}

// Legacy compatibility hooks - convert viem clients to ethers providers/signers
/** Hook to convert a viem Client to an ethers.js Provider with ChainKit initialization. */
export function useEthersProvider({ chainId }: { chainId?: number | undefined } = {}):
  | providers.JsonRpcProvider
  | undefined {
  const client = useClient<Config>({ chainId });

  return useMemo(() => {
    if (client) {
      // Initialize ChainKit for this chain
      // ensureChainKitInitialized(client.chain.id);
      return clientToProvider(client) as providers.JsonRpcProvider;
    }
    return undefined;
  }, [client]);
}

/** Hook to convert a Viem Client to an ethers.js Signer with ChainKit initialization. */
export function useEthersSigner({ chainId }: { chainId?: number } = {}): providers.JsonRpcSigner | undefined {
  const { data: client } = useConnectorClient<Config>({ chainId });

  return useMemo(() => {
    if (client) {
      // Initialize ChainKit for this chain
      // ensureChainKitInitialized(client.chain.id);
      return clientToSigner(client);
    }
    return undefined;
  }, [client]);
}

export function clearDisconnectFlag() {
  const wallets = getWalletConfigSetting();
  Object.values(wallets.walletConfig).forEach((wallet) => {
    wallet && localStorage.removeItem(`wagmi.${wallet.id}.disconnected`);
  });
}
